/**
 * compareGrid · «распредели черты по сетке Playwright / Selenium» (S16 · T08).
 * Per ADR-016. Таблица аспект × инструмент с пустыми ячейками + пул чипов-черт.
 * Тап чипа → тап ячейки (чип уезжает туда); тап чипа в ячейке → назад в пул.
 * «Сверить» проверяет: у каждого чипа data-cur === data-slot. 2D-размещение.
 *
 * Контракт:
 *   <div class="compare-grid" data-fin="мораль">
 *     <div class="cg-instr">…</div>
 *     <table class="cg-table"><thead><tr><th>Аспект</th><th>Playwright</th><th>Selenium</th></tr></thead>
 *       <tbody><tr><td>Ожидания</td>
 *         <td class="cg-cell" data-slot="wait-pw"></td>
 *         <td class="cg-cell" data-slot="wait-sel"></td></tr>…</tbody></table>
 *     <div class="cg-pool"><div class="cg-chip" data-slot="wait-pw">авто-ожидание<span class="cg-why">…</span></div>…</div>
 *     <button type="button" class="cg-check">Сверить</button>
 *     <div class="cg-verdict" aria-live="polite"></div>
 *   </div>
 *
 * No-JS: пул и таблица читаются. StrictMode-safe (guard + cleanup).
 */
export function initCompareGrid(root: HTMLElement): () => void {
  if (typeof document === 'undefined') return () => {};
  const cleanups: Array<() => void> = [];

  root.querySelectorAll<HTMLElement>('.compare-grid').forEach((el) => {
    if (el.dataset.cgInit === '1') return;
    el.dataset.cgInit = '1';
    const original = el.innerHTML;

    const pool = el.querySelector<HTMLElement>('.cg-pool');
    const checkBtn = el.querySelector<HTMLButtonElement>('.cg-check');
    const verdict = el.querySelector<HTMLElement>('.cg-verdict');
    const chips = Array.from(el.querySelectorAll<HTMLElement>('.cg-chip'));
    const cells = Array.from(el.querySelectorAll<HTMLElement>('.cg-cell'));
    if (!pool || !checkBtn || chips.length === 0 || cells.length === 0) return;

    el.classList.add('cg-ready');
    const total = chips.length;
    let selected: HTMLElement | null = null;

    const updateCount = () => {
      checkBtn.disabled = chips.filter((c) => c.dataset.cur).length !== total;
    };
    const clearMarks = () => {
      chips.forEach((c) => c.classList.remove('cg-wrong'));
      el.querySelectorAll<HTMLElement>('.cg-why.cg-show').forEach((w) => w.classList.remove('cg-show'));
      if (verdict) {
        verdict.textContent = '';
        verdict.className = 'cg-verdict';
      }
    };
    const select = (c: HTMLElement | null) => {
      if (selected) selected.classList.remove('cg-sel');
      selected = c;
      if (selected) selected.classList.add('cg-sel');
    };

    const onChip = (e: Event) => {
      if (el.classList.contains('cg-done')) return;
      const c = e.currentTarget as HTMLElement;
      clearMarks();
      if (c.dataset.cur) {
        delete c.dataset.cur;
        pool.appendChild(c);
        select(null);
        updateCount();
      } else {
        select(selected === c ? null : c);
      }
    };

    const onCell = (e: Event) => {
      if (!selected || el.classList.contains('cg-done')) return;
      const cell = e.currentTarget as HTMLElement;
      clearMarks();
      selected.dataset.cur = cell.dataset.slot ?? '';
      cell.appendChild(selected);
      select(null);
      updateCount();
    };

    const onCheck = () => {
      if (el.classList.contains('cg-done')) return;
      clearMarks();
      let wrong = 0;
      chips.forEach((c) => {
        if ((c.dataset.cur ?? '') !== (c.dataset.slot ?? '')) {
          c.classList.add('cg-wrong');
          c.querySelector<HTMLElement>('.cg-why')?.classList.add('cg-show');
          wrong += 1;
        }
      });
      if (wrong > 0) {
        if (verdict) {
          verdict.textContent = `Не на месте: ${wrong}. Открой подсказку у подсвеченных и переставь.`;
          verdict.className = 'cg-verdict cg-err';
        }
        return;
      }
      el.classList.add('cg-done');
      if (verdict) {
        verdict.textContent = el.dataset.fin || 'Верно. Концепты те же, разница — в ожиданиях и экосистеме.';
        verdict.className = 'cg-verdict cg-ok';
      }
    };

    chips.forEach((c) => {
      c.setAttribute('role', 'button');
      c.tabIndex = 0;
      c.addEventListener('click', onChip);
    });
    cells.forEach((cl) => {
      cl.setAttribute('role', 'button');
      cl.tabIndex = 0;
      cl.addEventListener('click', onCell);
    });
    checkBtn.addEventListener('click', onCheck);
    updateCount();

    cleanups.push(() => {
      chips.forEach((c) => c.removeEventListener('click', onChip));
      cells.forEach((cl) => cl.removeEventListener('click', onCell));
      checkBtn.removeEventListener('click', onCheck);
      el.innerHTML = original;
      el.classList.remove('cg-ready', 'cg-done');
      delete el.dataset.cgInit;
    });
  });

  return () => cleanups.forEach((fn) => fn());
}
